import { createAction, props } from '@ngrx/store';
import { ApprovalRequestDetail } from '@shared/models/approval.model';
import {
  ApprovalRequestItem,
  ApprovalFilters,
  ApprovalMode,
  ApprovalPagination,
  ApprovalSort,
  RequesterInfo,
} from '../models/approval.model';

/**
 * Load Approval Requests
 */
export const loadApprovalRequests = createAction(
  '[Approval] Load Approval Requests',
  props<{ mode?: ApprovalMode }>()
);

export const loadApprovalRequestsSuccess = createAction(
  '[Approval] Load Approval Requests Success',
  props<{
    data: ApprovalRequestItem[];
    pagination: ApprovalPagination;
  }>()
);

export const loadApprovalRequestsFailure = createAction(
  '[Approval] Load Approval Requests Failure',
  props<{ error: string }>()
);

/**
 * Tab Change
 */
export const setActiveTab = createAction(
  '[Approval] Set Active Tab',
  props<{ mode: ApprovalMode }>()
);

/**
 * Pagination Actions
 */
export const setPage = createAction(
  '[Approval] Set Page',
  props<{ pageIndex: number }>()
);

export const setPageSize = createAction(
  '[Approval] Set Page Size',
  props<{ pageSize: number }>()
);

/**
 * Sort Actions
 */
export const setSort = createAction(
  '[Approval] Set Sort',
  props<{ sort: ApprovalSort }>()
);

/**
 * Filter Actions
 */
export const setFilters = createAction(
  '[Approval] Set Filters',
  props<{ filters: ApprovalFilters }>()
);

export const clearFilters = createAction('[Approval] Clear Filters');

export const removeFilterChip = createAction(
  '[Approval] Remove Filter Chip',
  props<{ key: string; value?: string }>()
);

/**
 * Search Actions
 */
export const setSearch = createAction(
  '[Approval] Set Search',
  props<{ searchTerm: string }>()
);

/**
 * Requesters Actions (for filter dropdown)
 */
export const loadRequesters = createAction('[Approval] Load Requesters');

export const loadRequestersSuccess = createAction(
  '[Approval] Load Requesters Success',
  props<{ requesters: RequesterInfo[] }>()
);

export const loadRequestersFailure = createAction(
  '[Approval] Load Requesters Failure',
  props<{ error: string }>()
);

/**
 * Filter Modal Actions
 */
export const openFilterModal = createAction('[Approval] Open Filter Modal');

export const closeFilterModal = createAction('[Approval] Close Filter Modal');

// ---------------------------------------------------------------------------
// Detail
//
// Used by the `my-requests` detail page (and any future audience that
// renders a single approval request). Fetch goes through
// `SharedApprovalService.getApprovalRequestById` — the same endpoint
// the all-approvals / my-approvals pages call — but the slice owns the
// loading / error wiring here so cancel can compose cleanly on top.
// ---------------------------------------------------------------------------

export const loadApprovalRequestDetail = createAction(
  '[Approval] Load Detail',
  props<{ id: string }>()
);

export const loadApprovalRequestDetailSuccess = createAction(
  '[Approval] Load Detail Success',
  props<{ detail: ApprovalRequestDetail }>()
);

export const loadApprovalRequestDetailFailure = createAction(
  '[Approval] Load Detail Failure',
  props<{ error: string }>()
);

/** Drop the cached detail so a stale entry doesn't flash through. */
export const clearApprovalRequestDetail = createAction(
  '[Approval] Clear Detail'
);

// ---------------------------------------------------------------------------
// Cancel
//
// Only available on the `my-requests` audience (a requester can cancel
// their own pending request; they cannot approve / reject it). Mirrors
// the shape of `approveRequest` / `rejectRequest` but without the
// verification handshake — cancellation does not require a second
// factor today.
// ---------------------------------------------------------------------------

export const cancelApprovalRequest = createAction(
  '[Approval] Cancel',
  props<{ id: string; reason?: string }>()
);

export const cancelApprovalRequestSuccess = createAction(
  '[Approval] Cancel Success',
  props<{ id: string }>()
);

export const cancelApprovalRequestFailure = createAction(
  '[Approval] Cancel Failure',
  props<{ error: string }>()
);

/**
 * Reset State
 */
export const resetState = createAction('[Approval] Reset State');
